import { EntityId } from '@reduxjs/toolkit';
import { UpdateClientCrmFieldDTO } from 'app/main/apps/crm/types';
import { CouponFormType } from 'app/main/design-system/models/coupon.type';
import { RewardType } from 'app/main/design-system/models/reward.type';
import { FormElementType } from 'app/main/pages/clients/type';
import { LAYOUT_ELEMENT_TYPES, TEMP_ELEMENT_PREFIX } from './constants';

export { FormElementType };

export enum FormStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

// No longer offered by the builder; an old form still holding one must drop it to be saved.
export const RETIRED_ELEMENT_TYPES: ReadonlySet<FormElementType> = new Set([FormElementType.FILE_UPLOAD]);

export const CHOICE_ELEMENT_TYPES: ReadonlySet<FormElementType> = new Set([
  FormElementType.MULTIPLE_CHOICE,
  FormElementType.SINGLE_CHOICE,
  FormElementType.SELECT,
]);

export interface FormElement {
  id?: string;
  type: FormElementType;
  label: string;
  order: number;
  required: boolean;
  options?: string[] | null;
}

export enum FormAudience {
  CLIENT = 'CLIENT',
  MERCHANT = 'MERCHANT',
}

export enum FormEndLinkType {
  GOOGLE_REVIEW = 'GOOGLE_REVIEW',
  GAME = 'GAME',
  CUSTOM = 'CUSTOM',
}

export interface FinalBlockConfig {
  enabled: boolean;
  linkType: FormEndLinkType;
  googleReviewLink: string;
  gameId: string | null;
  customLink: string;
  buttonLabel: string;
  referralSmsMessage: string;
}

export const BUTTON_LABEL_MAX_LENGTH = 40;

export const DEFAULT_REFERRAL_SMS_MESSAGE = 'Merci {prénom} ! Parrainez vos proches et profitez de nos offres : {lien}';

export const DEFAULT_FINAL_BLOCK: FinalBlockConfig = {
  enabled: false,
  linkType: FormEndLinkType.GOOGLE_REVIEW,
  googleReviewLink: '',
  gameId: null,
  customLink: '',
  buttonLabel: '',
  referralSmsMessage: DEFAULT_REFERRAL_SMS_MESSAGE,
};

/**
 * Fills what a block saved by an older version lacks, and shows an empty message as
 * the built-in text it is sent as, instead of a blank field.
 */
export const withDefaultSmsMessage = (finalBlock?: Partial<FinalBlockConfig>): FinalBlockConfig => {
  const block = { ...DEFAULT_FINAL_BLOCK, ...finalBlock };
  return { ...block, referralSmsMessage: block.referralSmsMessage?.trim() || DEFAULT_REFERRAL_SMS_MESSAGE };
};

export const REFERRAL_SMS_MAX_LENGTH = 160;
// Worst case the API sizes the SMS with: a long first name and a full referral link.
const SAMPLE_FIRST_NAME = 'X'.repeat(20);
const SAMPLE_REFERRAL_LINK = 'X'.repeat(50);

export interface FormReward {
  name: string;
  type: RewardType;
  points?: number;
  couponData?: CouponFormType;
}

export const REWARD_NAME_MAX_LENGTH = 60;

export const DEFAULT_FORM_REWARD: FormReward = { name: '', type: RewardType.POINTS };

const rewardGivesPoints = (type: RewardType) => type !== RewardType.COUPON;
const rewardGivesCoupon = (type: RewardType) => type !== RewardType.POINTS;

export const isRewardValid = (reward?: FormReward | null): boolean => {
  if (!reward) return true;
  const name = reward.name.trim();
  if (!name || name.length > REWARD_NAME_MAX_LENGTH) return false;
  if (rewardGivesPoints(reward.type) && !(reward.points && reward.points > 0)) return false;
  return !rewardGivesCoupon(reward.type) || !!reward.couponData;
};

const toRewardPayload = ({ name, type, points, couponData }: FormReward): FormReward => ({
  name: name.trim(),
  type,
  ...(rewardGivesPoints(type) ? { points } : {}),
  ...(rewardGivesCoupon(type) && couponData
    ? {
        couponData: {
          title: couponData.title,
          data: couponData.data,
          validityPeriod: couponData.validityPeriod,
          terms: couponData.terms,
        },
      }
    : {}),
});

export interface FormOutDTO {
  storeIds: EntityId[];
  name: string;
  elements: FormElement[];
  finalBlock: FinalBlockConfig;
  audience: FormAudience;
  pointsCreditEnabled: boolean;
  /** Client forms only; null removes it. */
  reward?: FormReward | null;
}

export type FormUpdateDTO = Partial<FormOutDTO> & { status?: FormStatus };

export interface FormShareLink {
  storeId: string;
  url: string;
}

export interface FormInDTO extends FormOutDTO {
  id: EntityId;
  franchiseId: EntityId;
  status: FormStatus;
  slug: string;
  shareUrl: string;
  shareLinks?: FormShareLink[];
  responseCount?: number;
  dateCreated: string;
  lastModified: string;
  disabledAt: string | null;
}

// Picked field by field: a form saved before the referral rework still carries
// referralLink/capturePhone, which the API now refuses.
const toFinalBlockPayload = ({
  enabled,
  linkType,
  googleReviewLink,
  gameId,
  customLink,
  buttonLabel,
  referralSmsMessage,
}: FinalBlockConfig): FinalBlockConfig => ({
  enabled,
  linkType,
  googleReviewLink: googleReviewLink.trim(),
  gameId: linkType === FormEndLinkType.GAME ? gameId : null,
  customLink: customLink.trim(),
  buttonLabel: buttonLabel.trim(),
  referralSmsMessage: referralSmsMessage.trim(),
});

export const isTempElementId = (id?: string) => !id || id.startsWith(TEMP_ELEMENT_PREFIX);

const toElementPayload = (element: FormElement, index: number): FormElement => ({
  ...(isTempElementId(element.id) ? {} : { id: element.id }),
  type: element.type,
  label: element.label,
  order: index,
  required: element.required,
  options: CHOICE_ELEMENT_TYPES.has(element.type)
    ? (element.options ?? []).map(option => option.trim()).filter(Boolean)
    : null,
});

export const isHttpsUrl = (value: string): boolean => {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' && url.hostname.includes('.');
  } catch {
    return false;
  }
};

// The local file storage serves uploads over plain http on localhost.
const isLocalStorageUrl = (value: string): boolean => {
  if (process.env.NODE_ENV !== 'development') return false;
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' && url.hostname === 'localhost';
  } catch {
    return false;
  }
};

export const isFormImageUrl = (value: string): boolean => isHttpsUrl(value) || isLocalStorageUrl(value);

export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const IMAGE_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export type ImageFileProblem = 'type' | 'size';

export const imageFileProblem = (file: Pick<File, 'type' | 'size'>): ImageFileProblem | null => {
  if (!IMAGE_ACCEPTED_TYPES.includes(file.type)) return 'type';
  if (file.size > IMAGE_MAX_BYTES) return 'size';
  return null;
};

export const referralSmsLength = (message: string): number =>
  (message.trim() || DEFAULT_REFERRAL_SMS_MESSAGE)
    .replace(/\{pr[ée]nom\}/g, SAMPLE_FIRST_NAME)
    .replace(/\{lien\}/g, SAMPLE_REFERRAL_LINK).length;

export const isReviewLinkValid = (link: string): boolean => !link.trim() || isHttpsUrl(link);

export const isReferralSmsValid = (message: string): boolean =>
  (!message.trim() || message.includes('{lien}')) && referralSmsLength(message) <= REFERRAL_SMS_MAX_LENGTH;

export const isCustomLinkValid = (link: string): boolean => isHttpsUrl(link);

const isEndLinkValid = (finalBlock: FinalBlockConfig): boolean => {
  if (finalBlock.linkType === FormEndLinkType.CUSTOM) return isCustomLinkValid(finalBlock.customLink);
  if (finalBlock.linkType === FormEndLinkType.GAME) return !!finalBlock.gameId;
  return isReviewLinkValid(finalBlock.googleReviewLink);
};

export const isFinalBlockValid = (finalBlock?: FinalBlockConfig): boolean => {
  if (!finalBlock?.enabled) return true;
  return (
    isEndLinkValid(finalBlock) &&
    finalBlock.buttonLabel.trim().length <= BUTTON_LABEL_MAX_LENGTH &&
    isReferralSmsValid(finalBlock.referralSmsMessage)
  );
};

export const isElementValid = (element: FormElement): boolean => {
  if (RETIRED_ELEMENT_TYPES.has(element.type)) return false;
  // The CRM block shows the franchise's CRM fields: nothing of it is authored.
  if (element.type === FormElementType.CRM_JOURNEY) return true;
  if (!element.label.trim()) return false;
  if (element.type === FormElementType.IMAGE) return isFormImageUrl(element.label);
  if (!CHOICE_ELEMENT_TYPES.has(element.type)) return true;
  const filled = (element.options ?? []).filter(option => option.trim());
  return filled.length >= 2 && new Set(filled.map(option => option.trim())).size === filled.length;
};

export const isFormValid = (form: Partial<FormOutDTO>): boolean =>
  !!(
    form.name?.trim() &&
    form.storeIds &&
    form.storeIds.length > 0 &&
    form.elements &&
    form.elements.length > 0 &&
    form.elements.every(isElementValid) &&
    isFinalBlockValid(form.finalBlock) &&
    (form.audience === FormAudience.MERCHANT || isRewardValid(form.reward))
  );

export const convertFormToPayload = (form: Partial<FormOutDTO>): FormOutDTO => {
  if (!form.name?.trim()) {
    throw new Error('Form name is required');
  }
  if (!form.elements || form.elements.length === 0) {
    throw new Error('A form needs at least one element');
  }
  return {
    storeIds: form.storeIds ?? [],
    name: form.name,
    elements: form.elements.map(toElementPayload),
    finalBlock: toFinalBlockPayload(form.finalBlock ?? DEFAULT_FINAL_BLOCK),
    audience: form.audience ?? FormAudience.CLIENT,
    pointsCreditEnabled: form.audience === FormAudience.MERCHANT && !!form.pointsCreditEnabled,
    reward: form.audience !== FormAudience.MERCHANT && form.reward ? toRewardPayload(form.reward) : null,
  };
};

export type FormAnswerValue = string | number | string[] | null;

export type FormAnswers = Record<string, FormAnswerValue>;

export const isAnswerableElement = (element: FormElement): boolean =>
  !LAYOUT_ELEMENT_TYPES.includes(element.type) &&
  !RETIRED_ELEMENT_TYPES.has(element.type) &&
  element.type !== FormElementType.CRM_JOURNEY;

export const isAnswered = (value: FormAnswerValue | undefined): boolean =>
  value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);

export enum MerchantResponseOutcome {
  RECOGNIZED = 'RECOGNIZED',
  CARD_CREATED = 'CARD_CREATED',
  UNKNOWN_NUMBER = 'UNKNOWN_NUMBER',
  ANONYMOUS = 'ANONYMOUS',
}

export interface MerchantFormResponseRequest {
  storeId: string;
  phoneNumber?: string;
  answers: { elementId: string; value: FormAnswerValue }[];
  crmValues?: UpdateClientCrmFieldDTO[];
}

export interface FormRespondent {
  userId: string;
  firstName: string | null;
  lastName: string | null;
}

/** What answering earned. */
export interface FormRewardGranted {
  pointsCredited: number;
  couponTitle: string | null;
  pointsCeilingReached?: boolean;
}

export interface MerchantFormResponseResult {
  responseId: string;
  outcome: MerchantResponseOutcome;
  client: FormRespondent | null;
  reward?: FormRewardGranted | null;
  crmPointsCredited?: number | null;
}

export const toMerchantResponseRequest = (
  elements: FormElement[],
  answers: FormAnswers,
  storeId: string,
  phoneNumber: string,
  crmValues: UpdateClientCrmFieldDTO[] = [],
): MerchantFormResponseRequest => ({
  storeId,
  ...(phoneNumber.trim() ? { phoneNumber: phoneNumber.trim() } : {}),
  ...(crmValues.length ? { crmValues } : {}),
  answers: elements
    .filter(element => element.id && isAnswerableElement(element) && isAnswered(answers[element.id]))
    .map(element => ({
      elementId: element.id as string,
      value: toPayloadValue(element, answers[element.id as string]),
    })),
});

// A datetime-local input yields the store's wall-clock time with no zone; the public form sends UTC.
const toPayloadValue = (element: FormElement, value: FormAnswerValue): FormAnswerValue => {
  if (element.type !== FormElementType.DATETIME || typeof value !== 'string') return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type AnswerProblem = 'required' | 'email' | 'invalid';

/** The answers the dashboard can tell are wrong before sending, keyed by element id. */
export const answerProblems = (elements: FormElement[], answers: FormAnswers): Record<string, AnswerProblem> => {
  const problems: Record<string, AnswerProblem> = {};
  elements.forEach(element => {
    if (!element.id || !isAnswerableElement(element)) return;
    const value = answers[element.id];
    if (!isAnswered(value)) {
      if (element.required) problems[element.id] = 'required';
    } else if (element.type === FormElementType.EMAIL && !EMAIL_PATTERN.test(String(value).trim())) {
      problems[element.id] = 'email';
    }
  });
  return problems;
};

/** The stores a form can be filled in: its own, or every one when it was saved without any. */
export const formStores = <T extends { id: EntityId }>(form: Pick<FormInDTO, 'storeIds'>, stores: T[]): T[] =>
  form.storeIds.length ? stores.filter(store => form.storeIds.includes(store.id)) : stores;

export enum FormStatsPeriod {
  LAST_30_DAYS = '30d',
  LAST_90_DAYS = '90d',
  ALL = 'all',
}

export interface FormStatsOptionCount {
  /** null gathers answers to options the form no longer offers. */
  option: string | null;
  count: number;
  percentage: number;
}

export interface FormElementStats {
  elementId: string;
  label: string;
  type: FormElementType;
  answeredCount: number;
  average: number | null;
  minimum: number | null;
  maximum: number | null;
  distribution: FormStatsOptionCount[] | null;
}

export interface FormStats {
  formId: string;
  responseCount: number;
  elements: FormElementStats[];
}

export interface FormStatsFilters {
  storeId?: string;
  period: FormStatsPeriod;
}

export interface FormTextAnswer {
  responseId: string;
  value: FormAnswerValue;
  submittedAt: string;
  source: 'PUBLIC' | 'MERCHANT';
  client: FormRespondent | null;
}

export const TEXT_STATS_ELEMENT_TYPES: ReadonlySet<FormElementType> = new Set([
  FormElementType.SHORT_TEXT,
  FormElementType.LONG_TEXT,
  FormElementType.EMAIL,
  FormElementType.DATETIME,
]);
